import './App.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import axios from 'axios';
import { changeAntdTheme } from 'mini-dynamic-antd-theme';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import API from './api';
import AnalyticsEventsAPI from './api/AnalyticsEventsAPI';
import CommunityAPI from './api/CommunityAPI';
import config from './api/config';
import GamificationAPI from './api/GamificationAPI';
import UserAPI from './api/UserAPI';
import ScreenRotate from './assets/img/screen.png';
import CustomerSupportWidget from './components/CustomerSupportWidget';
import LikedByUsersModal
  from './components/LikedByUsersModal/LikedByUsersModal';
import TierExpiryModal
  from './components/TierExpiryModal/TierExpiryModal';
import {
  setAppReady,
  setBadges,
  setHostDataFailedCount,
  setHostMetadata,
  setIp,
  setLoggedIn,
  setNativeDomain,
  setUserDetailsFailedCount,
} from './context/appReducer';
import {
  setGroupChannelList,
  setMetadata,
} from './context/communityReducer';
import {
  setCreatorFreeMangoes,
  setCreatorMangoes,
} from './context/mangoReducer';
import { useTheme } from './context/ThemeProvider';
import { updateHostTitleConfig } from './context/thunks/appThunk';
import { updateUserCommunityAccess } from './context/thunks/communityThunk';
import {
  fetchPlatformIntegration,
} from './context/thunks/platformIntegrationThunk';
import {
  fetchAndSetUserDetails,
  updateUserTierDetails,
} from './context/thunks/userThunks';
import { useTQConnectConfig } from './hooks/queries/tqConnectQueries';
import Router from './Router';
import { REFRESH_TOKEN_KEY } from './shared/data';
import showAppError from './shared/error';
import {
  useAppDispatch,
  useAppSelector,
} from './shared/hooks';
import { ICOLORS } from './shared/Styles';
import {
  getLocalData,
  hexToHSL,
  setLocalData,
} from './shared/utils';
import { LocalStorageKeys } from './types/baseTypes';
import { IMango } from './types/feedTypes';
import { ROUTES } from './types/routes';
import {
  CreatorTierType,
  OnboardingStatus,
} from './types/userTypes';

const App: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateColors } = useTheme();
  const dispatch = useAppDispatch();

  const {
    appReady,
    hostMetadata: {
      appName,
      logo,
      creator: { _id: hostCreatorId },
      tqConnectConfig,
    },
    badges,
    isTagMango,
    userDetailsFailedCount,
    hostDataFailedCount,
    isCommunityEnabled,
    isTier3,
    isTier4,
  } = useAppSelector((state) => state.app);

  const { allCreatorMangoes, subscribedMangoes } = useAppSelector(
    (state) => state?.mango,
  );

  const userDetails = useAppSelector((state) => state?.user);

  const { groupChannelList } = useAppSelector((state) => state?.community);

  const host = useMemo(() => {
    let hostName = window.location.hostname;

    hostName.replace('www.', '');

    if (hostName.startsWith('m.')) {
      hostName = hostName.substring(2);
    }
    return hostName;
  }, []);

  const metaTitle = `${appName} Mobile`;
  const metaDescription = isTagMango
    ? 'TagMango helps content creators monetise better. It helps them host live workshops and launch their courses.'
    : `Check out your favourite creator ${appName}, to access your favourite workshops and courses.`;

  const fetchAllMangoes = async (userId: string) => {
    if (!userId) return;

    try {
      const [paidMangoesResp, freeMangoesResp] = await Promise.all([
        UserAPI.getCreatorMangoes(userId, true),
        UserAPI.getCreatorMangoes(userId, true, true),
      ]);

      if (paidMangoesResp.status === 200 && freeMangoesResp.status === 200) {
        const paidMangoes = (
          paidMangoesResp?.data?.result?.activeMangoes || []
        ).filter((mango) => !mango.isDeleted);

        const freeMangoes = (
          freeMangoesResp?.data?.result?.activeMangoes || []
        ).filter((mango) => !mango.isDeleted);

        dispatch(setCreatorMangoes(paidMangoes as IMango[]));
        dispatch(setCreatorFreeMangoes(freeMangoes as IMango[]));
      } else {
        if (paidMangoesResp.status !== 200) {
          showAppError(paidMangoesResp);
        }

        if (freeMangoesResp.status !== 200) {
          showAppError(freeMangoesResp);
        }
      }
    } catch (error) {
      showAppError(error);
    }
  };

  // fetch badges and set to host.badges
  const fetchGamificationBadges = async () => {
    // Gamification is only available on Tier 3+ hosts; the API returns 403 otherwise
    if (!userDetails?.id || !(isTier3 || isTier4)) return;

    if (!isTagMango) {
      const errorMsg = 'Failed to load badges';
      try {
        const resp = await GamificationAPI.getAllBadges();
        if (resp.status === 200) {
          if (resp.data?.result?.length > 0) {
            const badgesList = resp.data?.result;

            dispatch(setBadges(badgesList));
            // dispatch(setGamificationBadges(badgesList));
          }
        } else {
          showAppError(resp, errorMsg);
        }
      } catch (err) {
        showAppError(err, errorMsg);
      }
    }
  };

  const fetchUser = async () => {
    try {
      const newUserDetails = await dispatch(fetchAndSetUserDetails());
      if (newUserDetails) {
        dispatch(setLoggedIn(true));
        dispatch(setAppReady(true));
        dispatch(setUserDetailsFailedCount(0));

        // set user details in events tracking
        AnalyticsEventsAPI.registerUser(newUserDetails._id as string, {
          name: newUserDetails.name as string,
          email: newUserDetails.email as string,
          phone: newUserDetails.phone as number,
          onboarding: newUserDetails.onboarding as OnboardingStatus,
          host: newUserDetails.host || window.location.host,
          whitelabelPlanType: newUserDetails.whitelabelPlanType || 'free',
          userTier: newUserDetails?.userTier as CreatorTierType,
          score: newUserDetails?.score?.lifetime || 0,
          status: newUserDetails.isDeactivated ? 'deactivated' : 'active',
        });

        AnalyticsEventsAPI.addCommonEventProperties({
          host: newUserDetails.host || window.location.host,
          name: newUserDetails.name as string,
          email: newUserDetails.email as string,
          phone: newUserDetails.phone as number,
        });

        fetchAllMangoes(
          newUserDetails.onboarding === 'fan_completed'
            ? hostCreatorId
            : newUserDetails._id,
        );
      } else {
        // Increment failed count upto 3 times
        if (userDetailsFailedCount < 3) {
          dispatch(setUserDetailsFailedCount(userDetailsFailedCount + 1));
        } else {
          dispatch(setUserDetailsFailedCount(0));
          navigate(ROUTES.PLATFORM_CRASHED);
        }
      }
    } catch (error) {
      console.log(error);
      // Increment failed count upto 3 times
      if (userDetailsFailedCount < 3) {
        dispatch(setUserDetailsFailedCount(userDetailsFailedCount + 1));
      } else {
        // Redirect to crash page if 3 attempts have failed
        dispatch(setUserDetailsFailedCount(0));
        navigate(ROUTES.PLATFORM_CRASHED);
      }
    }
  };

  const fetchPlatformMetadata = async () => {
    try {
      const metadata = await API.getHostDetails();

      if (metadata.status === 200 && metadata.data.result._id) {
        dispatch(setHostMetadata(metadata.data.result));
        dispatch(updateHostTitleConfig());
        dispatch(setHostDataFailedCount(0));
        dispatch(updateUserTierDetails());

        if (
          metadata.data.result?.colors &&
          Object.keys(metadata.data.result?.colors).length > 0
        ) {
          let colors: Partial<ICOLORS> = metadata.data.result.colors;

          if (colors.PRIMARY) {
            changeAntdTheme(colors.PRIMARY);
          }

          if (colors.PRIMARY && !colors.PRIMARY_LIGHT) {
            colors = Object.assign(
              {
                PRIMARY_LIGHT: colors.PRIMARY_LIGHT || hexToHSL(colors.PRIMARY),
              },
              colors,
            );
          }

          if (metadata.data.result?.creator._id) {
            API.setCreator(metadata.data.result.creator._id);
          }

          updateColors(colors);
        }

        fetchAllMangoes(metadata.data.result.creator._id);
      }
    } catch (error) {
      console.log(error);
      // Increment failed count upto 3 times
      if (hostDataFailedCount < 3) {
        dispatch(setHostDataFailedCount(hostDataFailedCount + 1));
      } else {
        // Redirect to crash page if 3 attempts have failed
        dispatch(setHostDataFailedCount(0));
        navigate(ROUTES.PLATFORM_CRASHED);
      }
    }
  };

  const init = () => {
    // get current host
    let hostName = host;

    // hostName = 'unfilteredhq.com';
    // hostName = 'app.financewsharan.com';
    // hostName = 'learn.consciousbreathinghub.com';
    // hostName = 'dikshaarora.com';
    // hostName = 'learn.divyanshudamani.xyz';

    if (
      hostName.includes('tagmango.com') ||
      hostName.includes('localhost') ||
      hostName.includes('192')
    ) {
      API.setHost(config.HOST);
      dispatch(setNativeDomain(config.HOST));
      // Fetch platform metadata only if the host is a whitelabel domain
      if (!['tagmango.com', 'testing.tagmango.com'].includes(config.HOST)) {
        fetchPlatformMetadata();
      }
    } else {
      API.setHost(hostName);
      dispatch(setNativeDomain(hostName));
      fetchPlatformMetadata();
    }

    // check if user is logged in
    // let refreshToken = searchParams.get('token');
    // if (refreshToken) {
    //   localStorage.clear();
    //   setLocalData(refreshToken);
    //   localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    //   fetchUser();
    // } else {

    const token = getLocalData();

    if (token) {
      const verifyStatus = localStorage.getItem(LocalStorageKeys.VERIFY_STATUS);

      if (verifyStatus && verifyStatus === 'pending') {
        localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN_KEY);
        localStorage.removeItem(LocalStorageKeys.VERIFY_STATUS);
        setLocalData(null);
        dispatch(setAppReady(true));
        navigate(ROUTES.LOGIN);
        return;
      }

      setLocalData(token, () => {
        fetchUser();
      });
    } else {
      dispatch(setAppReady(true));
    }
    // }
  };

  const fetchMetaData = useCallback(
    async (channelIds: string[]) => {
      try {
        if (!channelIds || channelIds.length === 0) {
          return;
        }
        const resp = await CommunityAPI.getMetaData(channelIds);
        dispatch(setMetadata(resp?.data?.result?.metadata || []));
      } catch (error) {
        showAppError(error);
      }
    },
    [dispatch],
  );

  const fetchAllChannelData = useCallback(async () => {
    try {
      const resp = await CommunityAPI.getAllChannelGroups();
      if (resp.status === 200) {
        const channelGroups = resp?.data?.result;
        dispatch(setGroupChannelList(channelGroups));

        const channelIds = channelGroups?.reduce((acc: string[], item) => {
          const eachChannelIds = item?.channelArr?.map(
            (channel: any) => channel?._id,
          );
          return [...acc, ...eachChannelIds];
        }, []);
        fetchMetaData(channelIds);
      }
    } catch (error) {
      console.log(error);
    }
  }, [dispatch, fetchMetaData]);

  useEffect(() => {
    // console.log(window.location.host);
    // console.log(window.location);
    // when redirecting from checkout page, we get refresh token in query params
    const refreshToken = searchParams.get('token');
    // if refresh token is present in query params, set token in local storage
    // and fetch access token using this refresh token
    // then call init
    if (refreshToken) {
      // clearing local storage
      localStorage.clear();

      // setting refresh token in local storage for refreshToken API call
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

      API.refreshToken({
        refreshToken,
        tempTokenTime: 6 * 60 * 60, // passing tempTokenTime as that the refresh token stays there, and not get deleted in the backend
        callback: () => {
          init();
          navigate(ROUTES.HOME);
        },
      });
    }
    // else call init
    else {
      init();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry loading host details upto 3 times
  useEffect(() => {
    if (hostDataFailedCount > 0 && hostDataFailedCount <= 3) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostDataFailedCount]);

  // Retry loading user details upto 3 times
  useEffect(() => {
    if (userDetailsFailedCount > 0 && userDetailsFailedCount <= 3) {
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFailedCount]);

  useEffect(() => {
    if (appReady) {
      document.getElementById('favicon_main')?.setAttribute('href', logo);
      document.getElementById('favicon_apple')?.setAttribute('href', logo);
      document.title = metaTitle;
      document
        .getElementById('app_description')
        ?.setAttribute('content', metaDescription);
    }
  }, [metaTitle, metaDescription, logo, appReady]);

  // Store user's current public IP for use in mock vdoCipher overlay
  useEffect(() => {
    axios
      .get('https://api64.ipify.org/?format=json')
      .then((res) => dispatch(setIp(res.data.ip)))
      .catch((err) => {
        // message.error("Failed to obtain user's IP address");
        console.log(JSON.stringify(err));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appHeight = () => {
    setTimeout(() => {
      const doc = document.documentElement;
      doc.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
    }, 100);
  };

  // fetch TQ Connect config on initial load only if community feature is active that's handled in useTQConnectConfig hook
  useTQConnectConfig();

  useEffect(() => {
    window.addEventListener('resize', appHeight);
    appHeight();
    return () => window.removeEventListener('resize', appHeight);
  }, []);

  useEffect(() => {
    // initalize events tracking
    const hostName =
      host.includes('tagmango.com') ||
      host.includes('localhost') ||
      host.includes('192')
        ? config.HOST
        : host;
    dispatch(fetchPlatformIntegration(hostName));
    AnalyticsEventsAPI.init();
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [dispatch, host]);

  useEffect(() => {
    if (userDetails.id) {
      fetchGamificationBadges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails.id, isTier3, isTier4]);

  useEffect(() => {
    if (userDetails.id) {
      // update has community access when user is logged in
      dispatch(updateUserCommunityAccess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTagMango,
    hostCreatorId,
    tqConnectConfig?.enabled,
    tqConnectConfig?.allowedMangoes,
    isCommunityEnabled,
    badges,
    allCreatorMangoes,
    subscribedMangoes,
    userDetails.id,
    userDetails.type,
    userDetails?.userAccessPermissions,
    userDetails?.badges,
    userDetails?.isBlockedFromCommunityEngagement,
    groupChannelList,
  ]);

  useEffect(() => {
    if (userDetails.id) {
      fetchAllChannelData();
    }
  }, [fetchAllChannelData, userDetails.id]);

  return (
    <>
      <div className="landscape__wrapper">
        <div className="landscape__content">
          <img src={ScreenRotate} alt="logo" />
          <p className="landscape__content__text">
            Please rotate your device to portrait mode
          </p>
        </div>
      </div>
      {appReady ? (
        <>
          <Router />
          <LikedByUsersModal />
          <CustomerSupportWidget />
          <TierExpiryModal />
        </>
      ) : (
        <div className="loader__wrapper">
          <div className="lds-roller">
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(App);
